/**
 * PropertyStaffUsersTab — Property Manager → "Site Staff Users" tab.
 *
 * Restructured layout (Feb 2026):
 *   • Large, prominent cards (avatar circle, role badge, contact info)
 *   • Each card exposes a status pill (Active / Disabled) and three
 *     direct actions: Disable / Enable · Replace · Delete.
 *   • "Replace" opens the Add dialog pre-flagged: on successful create,
 *     the previous user is disabled automatically so the swap is atomic.
 *
 * API:
 *   GET    /api/properties/{propertyId}/staff-users
 *   POST   /api/properties/{propertyId}/staff-users
 *   PATCH  /api/properties/{propertyId}/staff-users/{userId}
 *   DELETE /api/properties/{propertyId}/staff-users/{userId}
 */
import { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Trash2, Mail, Phone, Shield, KeyRound, Copy, Pause, Play, RefreshCw, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import TabHelp from './TabHelp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TITLES = ['Owner', 'Regional Property Manager', 'Property Manager', 'Assistant Property Manager', 'Assistant Manager', 'Leasing Manager', 'Leasing Lead', 'Co-Manager', 'Maintenance Lead', 'Concierge Lead', 'Other'];

const initialForm = {
  first_name: '', middle_name: '', last_name: '', email: '', phone: '', extension: '',
  title: 'Assistant Manager', password: '',
};

export default function PropertyStaffUsersTab({ propertyId, propertyName }) {
  const [users, setUsers] = useState([]);
  const [dialogMode, setDialogMode] = useState(null); // null | 'add' | 'replace'
  const [replacingUser, setReplacingUser] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);

  useEffect(() => { fetchUsers(); }, [propertyId]);

  const fetchUsers = async () => {
    if (!propertyId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/properties/${propertyId}/staff-users`, { credentials: 'include' });
      if (res.ok) setUsers(await res.json());
    } catch (_) { /* ignore */ }
  };

  const genPassword = () => {
    const alpha = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let p = '';
    for (let i = 0; i < 10; i++) p += alpha[Math.floor(Math.random() * alpha.length)];
    setForm((f) => ({ ...f, password: p }));
  };

  const openAdd = () => { setForm(initialForm); setReplacingUser(null); setDialogMode('add'); };
  const openReplace = (u) => { setForm(initialForm); setReplacingUser(u); setDialogMode('replace'); };
  const closeDialog = () => { setDialogMode(null); setReplacingUser(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim() || !form.password) {
      toast.error('First name, last name, email and password are required');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/properties/${propertyId}/staff-users`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to create user');
        return;
      }
      const created = await res.json();
      // If this was a replacement, auto-disable the old user atomically.
      if (replacingUser) {
        await fetch(`${BACKEND_URL}/api/properties/${propertyId}/staff-users/${replacingUser.user_id}`, {
          method: 'PATCH', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ disabled: true }),
        });
        toast.success(`${replacingUser.name} replaced by ${created.name} — old access suspended.`);
      } else {
        toast.success(`${created.name} added — they can sign in now.`);
      }
      setLastCreated({ email: form.email, password: form.password });
      closeDialog();
      fetchUsers();
    } finally { setBusy(false); }
  };

  const toggleDisabled = async (u) => {
    const next = !u.disabled;
    const res = await fetch(`${BACKEND_URL}/api/properties/${propertyId}/staff-users/${u.user_id}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ disabled: next }),
    });
    if (res.ok) { toast.success(next ? 'Access suspended' : 'Access restored'); fetchUsers(); }
    else toast.error('Failed to update');
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name}? This cannot be undone.`)) return;
    const res = await fetch(`${BACKEND_URL}/api/properties/${propertyId}/staff-users/${u.user_id}`, {
      method: 'DELETE', credentials: 'include',
    });
    if (res.ok) { toast.success('Deleted'); fetchUsers(); }
  };

  const helpItems = [
    { q: 'Who should I add here?', a: 'Anyone on your property staff that needs the NECS Property Manager dashboard — assistants, leasing leads, co-managers, maintenance leads.' },
    { q: 'What does Disable do?', a: 'Suspends the user\'s sign-in without losing their record. Click "Enable" to restore access.' },
    { q: 'What does Replace do?', a: 'Opens the Add dialog so you can create the replacement. The moment they\'re saved, the person being replaced is auto-disabled — a clean swap.' },
    { q: 'When should I Delete instead?', a: 'Delete removes the record permanently. Use Disable for vacations / pending re-hires; use Delete only when the person is gone for good.' },
  ];

  const initials = (name) => (name || 'U').trim().split(/\s+/).map((s) => s[0]).join('').slice(0, 2).toUpperCase();
  const activeUsers = users.filter((u) => !u.disabled);
  const disabledUsers = users.filter((u) => u.disabled);

  const renderCard = (u) => (
    <Card
      key={u.user_id}
      className={`p-5 border-2 transition ${u.disabled ? 'border-zinc-200 bg-zinc-50 opacity-75' : 'border-[#C9A962]/40 hover:border-[#C9A962] bg-white'}`}
      data-testid={`site-staff-row-${u.user_id}`}
    >
      <div className="flex items-start gap-4">
        <div
          className="h-14 w-14 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-lg"
          style={{ backgroundColor: u.disabled ? '#e4e4e7' : '#C9A962', color: u.disabled ? '#71717a' : '#0a0a0a', fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          {initials(u.name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <p className="font-bold text-base truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.02em' }}>
              {u.name}
            </p>
            <Badge
              className={u.disabled ? 'bg-zinc-300 text-zinc-700' : 'bg-green-100 text-green-800 border border-green-300'}
              data-testid={`site-staff-status-${u.user_id}`}
            >
              {u.disabled ? '⏸ Disabled' : '● Active'}
            </Badge>
          </div>
          {u.title && <p className="text-xs text-zinc-600 uppercase tracking-wider mb-2">{u.title}</p>}
          <div className="text-[12px] text-zinc-700 space-y-0.5">
            <p className="flex items-center gap-1 truncate"><Mail className="h-3 w-3 text-[#C9A962]" /> {u.email}</p>
            {u.phone && <p className="flex items-center gap-1"><Phone className="h-3 w-3 text-[#C9A962]" /> {u.phone}{u.extension ? ` · ext. ${u.extension}` : ''}</p>}
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-zinc-200 flex flex-wrap gap-2">
        <Button
          type="button" size="sm" variant="outline"
          onClick={() => toggleDisabled(u)}
          className="h-8 text-xs font-bold"
          data-testid={`site-staff-toggle-${u.user_id}`}
        >
          {u.disabled ? <><Play className="h-3 w-3 mr-1" /> Enable</> : <><Pause className="h-3 w-3 mr-1" /> Disable</>}
        </Button>
        <Button
          type="button" size="sm" variant="outline"
          onClick={() => openReplace(u)}
          className="h-8 text-xs font-bold border-amber-400 text-amber-700 hover:bg-amber-50"
          data-testid={`site-staff-replace-${u.user_id}`}
        >
          <RefreshCw className="h-3 w-3 mr-1" /> Replace
        </Button>
        <Button
          type="button" size="sm" variant="outline"
          onClick={() => removeUser(u)}
          className="h-8 text-xs font-bold border-red-400 text-red-600 hover:bg-red-50 ml-auto"
          data-testid={`site-staff-delete-${u.user_id}`}
        >
          <Trash2 className="h-3 w-3 mr-1" /> Delete
        </Button>
      </div>
    </Card>
  );

  return (
    <div className="space-y-4" data-testid="property-staff-tab">
      <Card className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                SITE STAFF USERS
              </h2>
              <TabHelp title="Site Staff" items={helpItems} testid="site-staff-help" />
            </div>
            <p className="text-sm text-zinc-600 mt-1">
              {propertyName ? `Team members with PM access to ${propertyName}.` : 'Team members with PM access to this property.'}
            </p>
            <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
              <Badge className="bg-green-100 text-green-800 border border-green-300">● {activeUsers.length} Active</Badge>
              {disabledUsers.length > 0 && <Badge className="bg-zinc-200 text-zinc-700">⏸ {disabledUsers.length} Disabled</Badge>}
            </div>
          </div>
          <Dialog open={dialogMode !== null} onOpenChange={(o) => { if (!o) closeDialog(); }}>
            <DialogTrigger asChild>
              <Button onClick={openAdd} className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold" data-testid="site-staff-add-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Staff User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="site-staff-add-dialog">
              <DialogHeader>
                <DialogTitle>
                  {dialogMode === 'replace' && replacingUser
                    ? `Replace ${replacingUser.name}`
                    : 'Add Site Staff User'}
                </DialogTitle>
              </DialogHeader>
              {dialogMode === 'replace' && replacingUser && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-[12px] text-amber-900">
                  When you save, <strong>{replacingUser.name}</strong>&apos;s access will be automatically <strong>suspended</strong> and the new user will take over.
                </div>
              )}
              <form onSubmit={submit} className="space-y-4">
                {/* Unified NECS theme — every section wears the same
                    Charcoal / Gold uniform so the whole form reads as
                    Noble Empire. Empty inputs are outlined in a 2px gold
                    ring so the admin's eye jumps straight to what still
                    needs to be filled. Focus deepens to solid gold. */}

                {(() => null)()}
                {/* SECTION: Identity */}
                <div className="rounded-md border-2 p-3" style={{ borderColor: '#C9A962', background: 'linear-gradient(180deg, #FFFBEA 0%, #FFF7D6 100%)' }}>
                  <p className="text-[10px] uppercase tracking-[0.28em] font-bold mb-2" style={{ color: '#7a5a00', fontFamily: 'Barlow Condensed, sans-serif' }}>
                    · Identity
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-[11px] font-bold uppercase tracking-wider" style={{ color: '#0a0a0a' }}>First name *</Label>
                      <Input
                        data-testid="site-staff-first-name"
                        value={form.first_name}
                        onChange={(e) => setForm({...form, first_name: e.target.value})}
                        required
                        placeholder="Enter first name"
                        className={`bg-white text-[#0a0a0a] placeholder:text-[#C9A962]/70 font-medium border-2 ${form.first_name ? 'border-[#C9A962]' : 'border-[#C9A962]/60 ring-2 ring-[#C9A962]/30'} focus-visible:ring-[#FFD60A] focus-visible:border-[#FFD60A]`}
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] font-bold uppercase tracking-wider" style={{ color: '#0a0a0a' }}>Middle</Label>
                      <Input
                        value={form.middle_name}
                        onChange={(e) => setForm({...form, middle_name: e.target.value})}
                        placeholder="Optional"
                        className={`bg-white text-[#0a0a0a] placeholder:text-[#C9A962]/70 font-medium border-2 ${form.middle_name ? 'border-[#C9A962]' : 'border-[#C9A962]/60 ring-2 ring-[#C9A962]/30'} focus-visible:ring-[#FFD60A] focus-visible:border-[#FFD60A]`}
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] font-bold uppercase tracking-wider" style={{ color: '#0a0a0a' }}>Last name *</Label>
                      <Input
                        data-testid="site-staff-last-name"
                        value={form.last_name}
                        onChange={(e) => setForm({...form, last_name: e.target.value})}
                        required
                        placeholder="Enter last name"
                        className={`bg-white text-[#0a0a0a] placeholder:text-[#C9A962]/70 font-medium border-2 ${form.last_name ? 'border-[#C9A962]' : 'border-[#C9A962]/60 ring-2 ring-[#C9A962]/30'} focus-visible:ring-[#FFD60A] focus-visible:border-[#FFD60A]`}
                      />
                    </div>
                  </div>
                </div>

                {/* SECTION: Contact */}
                <div className="rounded-md border-2 p-4 sm:p-5" style={{ borderColor: '#C9A962', background: 'linear-gradient(180deg, #FFFBEA 0%, #FFF7D6 100%)' }}>
                  <p className="text-[10px] uppercase tracking-[0.28em] font-bold mb-2" style={{ color: '#7a5a00', fontFamily: 'Barlow Condensed, sans-serif' }}>
                    · Contact
                  </p>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <Label className="text-[11px] font-bold uppercase tracking-wider" style={{ color: '#0a0a0a' }}>Email *</Label>
                      <Input
                        type="email"
                        data-testid="site-staff-email"
                        value={form.email}
                        onChange={(e) => setForm({...form, email: e.target.value})}
                        required
                        placeholder="name@company.com"
                        className={`bg-white text-[#0a0a0a] placeholder:text-[#C9A962]/70 font-medium border-2 ${form.email ? 'border-[#C9A962]' : 'border-[#C9A962]/60 ring-2 ring-[#C9A962]/30'} focus-visible:ring-[#FFD60A] focus-visible:border-[#FFD60A]`}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_90px] gap-2">
                      <div>
                        <Label className="text-[11px] font-bold uppercase tracking-wider" style={{ color: '#0a0a0a' }}>Phone</Label>
                        <Input
                          value={form.phone}
                          onChange={(e) => setForm({...form, phone: e.target.value})}
                          placeholder="(555) 123-4567"
                          className={`bg-white text-[#0a0a0a] placeholder:text-[#C9A962]/70 font-medium border-2 ${form.phone ? 'border-[#C9A962]' : 'border-[#C9A962]/60 ring-2 ring-[#C9A962]/30'} focus-visible:ring-[#FFD60A] focus-visible:border-[#FFD60A]`}
                        />
                      </div>
                      <div>
                        <Label className="text-[11px] font-bold uppercase tracking-wider" style={{ color: '#0a0a0a' }}>Ext.</Label>
                        <Input
                          data-testid="site-staff-extension"
                          value={form.extension}
                          onChange={(e) => setForm({...form, extension: e.target.value})}
                          placeholder="x-101"
                          className={`bg-white text-[#0a0a0a] placeholder:text-[#C9A962]/70 font-medium border-2 ${form.extension ? 'border-[#C9A962]' : 'border-[#C9A962]/60 ring-2 ring-[#C9A962]/30'} focus-visible:ring-[#FFD60A] focus-visible:border-[#FFD60A]`}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* SECTION: Access & Title */}
                <div className="rounded-md border-2 p-4 sm:p-5" style={{ borderColor: '#C9A962', background: 'linear-gradient(180deg, #FFFBEA 0%, #FFF7D6 100%)' }}>
                  <p className="text-[10px] uppercase tracking-[0.28em] font-bold mb-2" style={{ color: '#7a5a00', fontFamily: 'Barlow Condensed, sans-serif' }}>
                    · Access &amp; Title
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[11px] font-bold uppercase tracking-wider" style={{ color: '#0a0a0a' }}>Title</Label>
                      <Select value={form.title} onValueChange={(v) => setForm({...form, title: v})}>
                        <SelectTrigger
                          data-testid="site-staff-title"
                          className="bg-white text-[#0a0a0a] font-medium border-2 border-[#C9A962] focus:ring-[#FFD60A] focus:border-[#FFD60A]"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TITLES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px] font-bold uppercase tracking-wider" style={{ color: '#0a0a0a' }}>Password *</Label>
                      <div className="flex gap-2">
                        <Input
                          data-testid="site-staff-password"
                          value={form.password}
                          onChange={(e) => setForm({...form, password: e.target.value})}
                          required
                          placeholder="At least 8 chars"
                          className={`bg-white text-[#0a0a0a] placeholder:text-[#C9A962]/70 font-mono font-bold border-2 ${form.password ? 'border-[#C9A962]' : 'border-[#C9A962]/60 ring-2 ring-[#C9A962]/30'} focus-visible:ring-[#FFD60A] focus-visible:border-[#FFD60A]`}
                        />
                        <Button
                          type="button"
                          onClick={genPassword}
                          data-testid="site-staff-gen-pw"
                          className="bg-[#0a0a0a] text-[#C9A962] hover:bg-zinc-800 border-2 border-[#C9A962] font-bold"
                        >
                          <KeyRound className="h-3 w-3 mr-1" /> Gen
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full font-bold text-base tracking-wider"
                  style={{
                    backgroundColor: '#FFD60A',
                    color: '#0a0a0a',
                    border: '2px solid #C9A962',
                    letterSpacing: '0.08em',
                  }}
                  data-testid="site-staff-submit"
                >
                  {busy ? 'Saving…' : dialogMode === 'replace' ? 'Save Replacement & Suspend Old' : 'Create & Grant Access'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {lastCreated && (
          <div className="mb-5 p-3 rounded border border-green-300 bg-green-50" data-testid="site-staff-last-created">
            <p className="text-xs uppercase tracking-wider font-bold text-green-800 mb-1">Credentials to share</p>
            <p className="text-sm text-zinc-700 flex items-center gap-2">
              <Mail className="h-4 w-4 text-green-700" /> <span className="font-mono">{lastCreated.email}</span>
              <button onClick={() => { navigator.clipboard.writeText(lastCreated.email); toast.success('Email copied'); }} className="text-zinc-500 hover:text-zinc-900"><Copy className="h-3 w-3" /></button>
            </p>
            <p className="text-sm text-zinc-700 flex items-center gap-2 mt-1">
              <KeyRound className="h-4 w-4 text-green-700" /> <span className="font-mono">{lastCreated.password}</span>
              <button onClick={() => { navigator.clipboard.writeText(lastCreated.password); toast.success('Password copied'); }} className="text-zinc-500 hover:text-zinc-900"><Copy className="h-3 w-3" /></button>
            </p>
            <button onClick={() => setLastCreated(null)} className="text-[11px] text-zinc-500 italic underline mt-1">Dismiss</button>
          </div>
        )}

        {users.length === 0 ? (
          <div className="text-center py-12 text-zinc-500 border-2 border-dashed border-zinc-200 rounded-lg">
            <UserIcon className="h-10 w-10 mx-auto mb-2 text-zinc-300" />
            <p className="text-sm">No site staff yet — add your first team member above.</p>
          </div>
        ) : (
          <>
            {activeUsers.length > 0 && (
              <>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2">Active · {activeUsers.length}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeUsers.map(renderCard)}
                </div>
              </>
            )}
            {disabledUsers.length > 0 && (
              <>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mt-6 mb-2">Disabled · {disabledUsers.length}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {disabledUsers.map(renderCard)}
                </div>
              </>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
